/**
 * StayMii Date/Time Formatting Utilities
 * 
 * Standard formats:
 * - Display date: DD-MM-YYYY (e.g., 28-02-2026)
 * - Display time: 12-hour (e.g., 09:00 AM)
 * - Timezone: Asia/Kuala_Lumpur (UTC+8)
 * - API format: DD-MM-YYYY
 * - Datetime format: DD-MM-YYYYTHH:mm:ss+08:00
 */

import { format, parse, parseISO, isValid } from 'date-fns';

// Timezone offset for Malaysia (UTC+8)
export const TIMEZONE = 'Asia/Kuala_Lumpur';
export const TIMEZONE_OFFSET = '+08:00';

/**
 * Format date to DD-MM-YYYY
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'dd-MM-yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date to display format with day name (e.g., "Fri, 28-02-2026")
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string with day
 */
export const formatDateWithDay = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'EEE, dd-MM-yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date to long format (e.g., "28 February 2026")
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'dd MMMM yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date to month/year (e.g., "February 2026")
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted month/year string
 */
export const formatMonthYear = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'MMMM yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format time to 12-hour format (e.g., "09:00 AM")
 * @param {Date|string} time - Date object or time string (HH:mm or full datetime)
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  if (!time) return '';
  try {
    // Handle HH:mm format
    if (typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
    const dateObj = typeof time === 'string' ? parseISO(time) : time;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'hh:mm a');
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

/**
 * Format datetime to DD-MM-YYYY HH:mm AM/PM
 * @param {Date|string} datetime - Date object or datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return '';
  try {
    const dateObj = typeof datetime === 'string' ? parseISO(datetime) : datetime;
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'dd-MM-yyyy hh:mm a');
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
};

/**
 * Format datetime for API (DD-MM-YYYYTHH:mm:ss+08:00)
 * @param {Date|string} datetime - Date object or datetime string
 * @returns {string} ISO formatted datetime with timezone
 */
export const formatDateTimeAPI = (datetime) => {
  if (!datetime) return '';
  try {
    const dateObj = typeof datetime === 'string' ? parseISO(datetime) : datetime;
    if (!isValid(dateObj)) return '';
    return format(dateObj, "dd-MM-yyyy'T'HH:mm:ss") + TIMEZONE_OFFSET;
  } catch (error) {
    console.error('Error formatting datetime for API:', error);
    return '';
  }
};

/**
 * Format date for API (DD-MM-YYYY)
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date for API
 */
export const formatDateAPI = (date) => {
  return formatDate(date);
};

/**
 * Parse DD-MM-YYYY string to Date object
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateDMY = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Handle both DD-MM-YYYY and YYYY-MM-DD formats
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      return parseISO(dateStr);
    }
    return parse(dateStr, 'dd-MM-yyyy', new Date());
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Convert YYYY-MM-DD to DD-MM-YYYY
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Date string in DD-MM-YYYY format
 */
export const convertYMDtoDMY = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr; // Already in DD-MM-YYYY
  try {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  } catch (error) {
    return dateStr;
  }
};

/**
 * Convert DD-MM-YYYY to YYYY-MM-DD (for API calls that need ISO format)
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const convertDMYtoYMD = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr; // Already in YYYY-MM-DD
  try {
    const [day, month, year] = dateStr.split('-');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return dateStr;
  }
};

/**
 * Get current date in DD-MM-YYYY format (Malaysia timezone)
 * @returns {string} Current date
 */
export const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Get current datetime in display format (Malaysia timezone)
 * @returns {string} Current datetime
 */
export const getCurrentDateTime = () => {
  return formatDateTime(new Date());
};

/**
 * Format relative date description
 * @param {Date|string} date - Date to describe
 * @returns {string} Relative description (e.g., "Today", "Yesterday", "28-02-2026")
 */
export const formatRelativeDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return '';
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dateStr = format(dateObj, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    
    if (dateStr === todayStr) return 'Today';
    if (dateStr === yesterdayStr) return 'Yesterday';
    if (dateStr === tomorrowStr) return 'Tomorrow';
    
    return formatDate(dateObj);
  } catch (error) {
    return formatDate(date);
  }
};

export default {
  formatDate,
  formatDateWithDay,
  formatDateLong,
  formatMonthYear,
  formatTime,
  formatDateTime,
  formatDateTimeAPI,
  formatDateAPI,
  parseDateDMY,
  convertYMDtoDMY,
  convertDMYtoYMD,
  getCurrentDate,
  getCurrentDateTime,
  formatRelativeDate,
  TIMEZONE,
  TIMEZONE_OFFSET
};
